import { useQuery } from '@tanstack/react-query'
import { ShieldOff } from 'lucide-react'
import { useMemo } from 'react'
import { Outlet } from 'react-router-dom'

import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { Card } from '../components/ui/card'
import { Skeleton } from '../components/ui/primitives'
import { ApiError } from '../lib/api'
import { roleNames } from '../lib/authz'
import { meQuery, useAuthz } from './authz'
import { buildAgentNav } from './nav'
import { NotificationCenter } from './components/NotificationCenter'
import { OrgScopeSelect } from './components/OrgScopeSelect'
import { OrgScopeProvider } from './scope'

/** The gate on `/agent`.
 *
 * Deliberately no chrome of its own: the console has two shells — the service
 * desk's and the administration console's — and which one you get is a routing
 * decision, not a branch inside a layout. What is common to both is the thing
 * that has to happen before either: establishing that the caller is on the desk
 * at all, and providing the organisation scope every screen filters by.
 */
export function AgentLayout() {
  // Unscoped: the shell loads before the scope provider exists, and this is the
  // request that establishes whether the caller is on the desk at all. Screens
  // inside the provider use `useAuthz`, which shares this cache entry until an
  // organisation is actually chosen.
  const { data, isLoading, error } = useQuery({
    ...meQuery(null),
    retry: (_count, err) => !(err instanceof ApiError && [401, 403].includes(err.status)),
  })

  const denied = error instanceof ApiError && [401, 403].includes(error.status)

  if (denied) {
    return (
      <div className="grid min-h-screen place-items-center bg-surface p-6">
        <Card className="max-w-md p-6 text-center">
          <span className="mx-auto grid size-10 place-items-center rounded-lg bg-muted text-muted-foreground">
            <ShieldOff className="size-5" />
          </span>
          <h1 className="mt-3 text-base font-semibold">This area is for service desk agents</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Your account has no active agent record. If you need access, ask an administrator to
            create one.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-4">
            <a href="/">Go to the portal</a>
          </Button>
        </Card>
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="flex h-screen bg-surface">
        <div className="hidden w-60 shrink-0 bg-sidebar lg:block" />
        <div className="flex-1 space-y-4 p-6">
          <Skeleton className="h-8 w-52" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-[4.5rem]" />
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-56" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <OrgScopeProvider>
      <Outlet />
    </OrgScopeProvider>
  )
}

/** The service desk's shell. Every route except the administration tree. */
export function AgentConsoleLayout() {
  // Shares the gate's cache entry, so this costs no request.
  const { data } = useQuery(meQuery(null))
  // Scope-aware, unlike the line above: modules are licensed per organisation,
  // so which rows exist can change when the scope control does. Fails closed —
  // before the payload arrives `can` is false everywhere and the rail is bare,
  // which is the right way round for a navigation tree.
  const authz = useAuthz()
  const nav = useMemo(() => buildAgentNav(authz), [authz])

  return (
    <AppShell
      nav={nav}
      user={{
        name: data?.name ?? '',
        email: data?.email ?? '',
        roles: roleNames(data),
      }}
      scopeControl={<OrgScopeSelect />}
      notifications={<NotificationCenter />}
    >
      <Outlet />
    </AppShell>
  )
}
